import type { History } from 'history'
import { TypedStartListening } from '@reduxjs/toolkit'
import type { UseMutationResult } from '@tanstack/react-query'
import type { FeatureFlagKeyName, FeatureValueType } from '@mobi/settings/featureFlags'
import type { globalToastEmitter } from '@mobi/component-library/Feedback/Toast/helpers/events'
import type { ToastEmitter } from '@mobi/component-library/Feedback/Toast/types'
import type { BetSlipBetsState } from '../Store/Bets'
import type {
  BetSlipItem,
  BetSlipResponse,
  Campaign,
  FobBetReceiptResponse,
  SplitBonusBetInput,
  CampaignV2,
  BetSource,
  EventDetails,
} from '../types'
import type { AppState } from '../Store'

let hostHelpers: HostHelpers

/** Set host functions in module scope (NOT inside react) */
export function setHostHelpers(initHelpers: HostHelpers) {
  hostHelpers = initHelpers
}

export function getHostHelpers(): HostHelpers {
  return hostHelpers
}

// =====
// Types
// =====

export type HostHelpers = {
  redux: { startListening: TypedStartListening<AppState> }
  history: History

  postAllBetsToServer: (
    queryType: 'propose' | 'confirm' | 'refresh',
    state: Pick<BetSlipBetsState, 'items' | 'multiBetError' | 'multiInvestment' | 'multiReceipt'>
  ) => Promise<BetSlipResponse[]>

  /** Primarily used in MOBI */
  toggleBetSlipDrawer?(isOpen: boolean): void

  hostRoutes: {
    Sports: string
    Meetings: string
  }

  handleShareBet(share: {
    items: BetSlipItem[]
    isBetPlaced: boolean
    toastEmitter?: ToastEmitter
    multiReceipt?: FobBetReceiptResponse
    ticketSequence?: number
    betSource?: BetSource
  }): void

  canShareBet(): boolean
  openDepositModal(): void
  showLogin(): void

  isReactNativeApp?: boolean

  useFeature: <T extends FeatureFlagKeyName>(flag: T) => FeatureValueType<T> | boolean

  BonusBetSplittingTray: ({
    bonusBetToSplit,
    isTrayOpen,
    closeTray,
    confirmSplitBonusBet,
    isBonusBetSplitPending,
  }: {
    bonusBetToSplit: CampaignV2 | null
    isTrayOpen: boolean
    closeTray: () => void
    confirmSplitBonusBet: (data: SplitBonusBetInput) => void
    isBonusBetSplitPending: boolean
  }) => JSX.Element | null

  groupBonusBets: (bonusBets: Campaign[]) => Record<string, Campaign[]>

  useSplitBonusBetMutation: ({
    updateBonusBetToSplit,
    toastEmitter,
  }: {
    updateBonusBetToSplit: () => void
    toastEmitter?: typeof globalToastEmitter
  }) => UseMutationResult<void, Error, SplitBonusBetInput, unknown>

  isCampaignV2: (campaign: Campaign | CampaignV2) => campaign is CampaignV2

  trackBonusOfferClick: (campaign: Campaign, element_clicked: string) => void

  SetAlertNotificationButton: React.FC<{ selectionDetails: EventDetails | null }>
}
